import React, { useEffect, useState } from 'react';
import { X, Download, Share, PlusSquare } from 'lucide-react';
import { Button } from '../ui/button';

const DISMISS_KEY = 'bc_install_dismissed_at';
const DISMISS_DAYS = 14;

const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const recentlyDismissed = () => {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < DISMISS_DAYS * 86400000;
};

export const InstallBanner = () => {
  const [promptEvent, setPromptEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setPromptEvent(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    let iosTimer;
    if (isIos()) iosTimer = setTimeout(() => setVisible(true), 4000);
    const onInstalled = () => setVisible(false);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (promptEvent) {
      promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      if (outcome === 'accepted') setVisible(false);
      else dismiss();
    } else {
      setShowIosHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-36 md:bottom-6 inset-x-3 md:inset-x-auto md:right-24 md:w-[380px] z-[60] animate-in slide-in-from-bottom-4" data-testid="install-banner">
      <div className="rounded-xl bg-[#1E3A8A] text-white shadow-2xl p-4 flex gap-3 items-start">
        <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm">Installer l'app BuildConnect</p>
          {showIosHelp ? (
            <p className="text-xs text-blue-100 mt-1 leading-relaxed">
              Appuyez sur <Share className="inline w-3.5 h-3.5 -mt-0.5" /> <strong>Partager</strong> puis <PlusSquare className="inline w-3.5 h-3.5 -mt-0.5" /> <strong>« Sur l'écran d'accueil »</strong>.
            </p>
          ) : (
            <p className="text-xs text-blue-100 mt-1">Accès rapide depuis votre écran d'accueil, même sur chantier.</p>
          )}
          {!showIosHelp && (
            <Button size="sm" className="mt-2 bg-[#F59E0B] hover:bg-[#d97706] text-white h-8" onClick={install} data-testid="install-app-btn">
              <Download className="w-4 h-4 mr-1" /> Installer
            </Button>
          )}
        </div>
        <button onClick={dismiss} className="p-1 rounded hover:bg-white/10" aria-label="Fermer" data-testid="install-banner-close">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const OfflineBanner = () => {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  if (!offline) return null;
  return (
    <div className="bg-[#F59E0B] text-white text-center text-xs font-medium py-1.5 px-4" data-testid="offline-banner">
      Vous êtes hors ligne — affichage des dernières données enregistrées
    </div>
  );
};
